// toggle-header.component.ts
import { Component, Output, EventEmitter, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipComponent } from '../ui/tooltip/tooltip.component';
import { IconComponent, IconName } from '../../icons/icon.component';

export type ViewMode = 'graph' | 'table' | string;
export type PixelValue = `${number}px`;
type colorCode = `#${string}`;
export interface toggleHeader {
  heading?: string;
  showTable?: boolean;
  showlabels?: boolean;
  showToggleButton?: boolean;
  headingColor?: colorCode;
  headingCursor?: string;
  headerChangeView?: boolean;
  showHeading?: boolean;
  iconPath?: string;
  showIcon?: boolean;
  fontSize?: PixelValue;
  toggleIconPath?: string;
  size?: 'sm' | 'md' | 'lg'; // Size customization
  activeTextColor?: colorCode; // Active button text color
  inactiveTextColor?: colorCode; // Inactive button text color
  activeBgColor?: colorCode; // Active button background color
  toggleOptions?: {
    label?: string;
    icon?: string;
    tooltip?: string;
    isTable?: boolean;
    iconName?: IconName;
    value?: string | number;  // New: custom value to emit
  }[];
}

@Component({
  selector: 'app-toggle-btns',
  templateUrl: './toggle-btns.component.html',
  styleUrl: './toggle-btns.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TooltipComponent, CommonModule, IconComponent],
})
export class ToggleBtnsComponent implements OnInit {
  isDark = () => false; // Fallback or inject ThemeService if needed
  @Input() headerOptions: toggleHeader = {
    heading: '',
    showTable: false,
    showlabels: true,
    showToggleButton: true,
    headingColor: '#131921',
    headingCursor: '',
    headerChangeView: false,
    showHeading: true,
    iconPath: '',
    showIcon: true,
    fontSize: '16px',
    size: 'md',
    activeTextColor: '#AD46FF', // Default active text color
    inactiveTextColor: '#7D7D93', // Default inactive text color
    activeBgColor: '#FFFFFF', // Default active background color
    toggleOptions: [],
  };

  @Input() showTrendingIcon: boolean = false;
  @Input() bgColor: string = '';
  @Input() activeValue?: string | number; // Allow parent to specify active value for custom options
  @Input() auditContext: string = '';

  @Output() viewChanged = new EventEmitter<ViewMode>();

  getActiveIndex(): number {
    if (!this.headerOptions.toggleOptions || this.headerOptions.toggleOptions.length === 0) {
      return 0;
    }
    
    // If activeValue is provided and options have values, match by value
    if (this.activeValue !== undefined) {
      const activeIndex = this.headerOptions.toggleOptions.findIndex(
        opt => opt.value !== undefined && opt.value === this.activeValue
      );
      if (activeIndex !== -1) {
        return activeIndex;
      }
    }
    
    // Fallback to isTable matching showTable (for backward compatibility)
    const activeIndex = this.headerOptions.toggleOptions.findIndex(
      opt => (opt.isTable ?? false) === (this.headerOptions.showTable ?? false)
    );
    
    return activeIndex !== -1 ? activeIndex : 0;
  }

  getActiveTextColor(): string {
    return this.headerOptions.activeTextColor || '#AD46FF';
  }

  getInactiveTextColor(): string {
    return this.headerOptions.inactiveTextColor || '#7D7D93';
  }

  toggleView(toTable: boolean, option?: any) {
    // Check if the clicked option is already active
    if (option?.value !== undefined && option.value === this.activeValue) return;
    if (option?.value === undefined && this.headerOptions.showTable === toTable) return;

    this.headerOptions.showTable = toTable;
    
    // If option has a custom value, emit that; otherwise emit 'table' or 'graph'
    if (option?.value !== undefined) {
      this.viewChanged.emit(option.value);
    } else {
      this.viewChanged.emit(this.headerOptions.showTable ? 'table' : 'graph');
    }
  }

  ngOnInit() {

    if (!this.headerOptions) {
      this.headerOptions = {};
    }

    if (
      !this.headerOptions.toggleOptions ||
      this.headerOptions.toggleOptions.length === 0
    ) {
      this.headerOptions.toggleOptions = [
        {
          label: 'Graph',
          icon: 'assets/img/PNG/Vector.png',
          tooltip: 'Click to View Graph',
          isTable: false,
          iconName: 'chartLine',
        },
        {
          label: 'Table',
          icon: 'assets/img/PNG/show_table.png',
          tooltip: 'Click to View Table',
          isTable: true,
          iconName: 'betweenHorizontalStart',
        },
      ];
    }
    
    // Always ensure iconName is set for all options
    if (this.headerOptions.toggleOptions) {
      this.headerOptions.toggleOptions = this.headerOptions.toggleOptions.map(
        (option) => {
          if (!option.iconName) {
            option.iconName = option.isTable
              ? 'betweenHorizontalStart'
              : 'chartLine';
          }
          return option;
        }
      );
    }
  }
}
